import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Home,
  PlusCircle,
  LayoutDashboard,
  Heart,
  InfoIcon,
  LogIn,
  UserPlus,
  LogOut,
  User,
  Users,
  Menu,
  X,
  Map as MapIcon,
  MapPin,
  MessageCircle,
  ChevronDown,
} from "lucide-react";
import { MessagesNavButton } from "../../chat/components/MessagesNavButton";
import { formatBadge, useUnreadCount } from "../../chat/hooks/useUnreadCount";
import { LanguageIconMenu, LanguageSelect } from "./LanguageSwitcher";
import { useAuth } from "../../hooks/useAuth";
import { useAuthStore } from "../../store/useAuthStore";
import BottomNav from "./BottomNav";
import { ProTabBar } from "./ProTabBar";
import { proEnabled } from "../../pro/flags";

export function Header() {
  const { t } = useTranslation();
  const isAuthenticated = useAuth();
  const onFavorites = useLocation().pathname.startsWith("/favorites");
  const unreadBadge = formatBadge(useUnreadCount());

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const {
    name,
    avatar,
    logout: logoutFromStore,
  } = useAuthStore((state) => state);

  const [isScrolled, setIsScrolled] = useState(false);

  const toggleProfileMenu = () => setProfileMenuOpen(!profileMenuOpen);
  const closeProfileMenu = () => setProfileMenuOpen(false);

  const logout = () => {
    logoutFromStore();
    closeProfileMenu();
    setMobileMenuOpen(false);
  };

  // ─── Scroll shadow ──────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ─── Click outside profile menu ─────────────────────────────────
  const menuRef = useRef<HTMLDivElement>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!moreOpen) return;
    const close = (e: MouseEvent) => {
      if (!moreRef.current?.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [moreOpen]);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeProfileMenu();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ─── Escape closes profile menu ──────────────────────────────────
  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") closeProfileMenu();
    }
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
  }, [mobileMenuOpen]);

  // ─── Nav links (common for all users) ───────────────────────────
  const commonNavLinks = [
    { to: "/listings", label: t("nav.browse"), icon: Home },
    // TURAQ Pro (VITE_PRO / ?pro=1): new buildings catalogue.
    ...(proEnabled ? [{ to: "/novostroyki", label: t("nav.newBuildings", { defaultValue: "Новостройки" }), icon: Building2 }] : []),
    {
      to: "/specialists",
      label: t("nav.specialists", { defaultValue: "Специалисты" }),
      icon: Users,
    },
    { to: "/map-page", label: t("nav.kyrgyzMap"), icon: MapPin },
    { to: "/create-listing", label: t("nav.post"), icon: PlusCircle },
    { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard },
    { to: "/favorites", label: t("nav.favorites"), icon: Heart },
    { to: "/aboutus", label: t("nav.aboutUs"), icon: InfoIcon },
    { to: "/turaq-kg-genplan-bishkek2050", label: t("nav.genplan"), icon: MapIcon },
  ];

  // ─── Auth-aware mobile drawer nav ───────────────────────────────
  const mobileDrawerNavArray = isAuthenticated
    ? [
        ...commonNavLinks.slice(0, proEnabled ? 7 : 6),
        { to: "/messages", label: t("nav.messages", "Сообщения"), icon: MessageCircle },
        ...commonNavLinks.slice(proEnabled ? 7 : 6),
      ]
    : [
        ...commonNavLinks,
        { to: "/login", label: t("nav.login"), icon: LogIn },
        { to: "/register", label: t("nav.register"), icon: UserPlus },
      ];
  return (
    <>
      <header
        data-chrome="header"
        className={`sticky top-0 z-[99] border-b border-gray-200 shadow-sm transition-all duration-300 pt-safe-top`}
        style={{
          backgroundColor: isScrolled
            ? "rgba(255,255,255,1)"
            : "rgba(255,255,255,1)",
        }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link
              to="/"
              className="flex shrink-0 items-center gap-2 mr-6 2xl:mr-10 text-2xl sm:text-3xl font-bold text-blue-600"
            >
              TURAQ.KG
            </Link>

            {/* 8 links + language + account don't fit in 1280–1535 px (the
                last links ran under the language switcher, the logo touched
                «Жарыялар»): there the last two go into «Дагы ▾». */}
            <nav className="hidden xl:flex shrink-0 items-center gap-3.5 2xl:gap-6 text-[15px] 2xl:text-base whitespace-nowrap">
              <Link to="/listings" className="hover:text-blue-600">
                {t("nav.browse")}
              </Link>
              {/* Inline only where it fits (2xl); below that it lives in «Ещё». */}
              {proEnabled && (
                <Link to="/novostroyki" className="hidden 2xl:inline hover:text-blue-600">
                  {t("nav.newBuildings", { defaultValue: "Новостройки" })}
                </Link>
              )}
              <Link
                to="/map-page"
                className="hover:text-blue-600"
                target="_blank"
                rel="noopener"
              >
                {t("nav.kyrgyzMap")}
              </Link>
              <Link to="/specialists" className="hover:text-blue-600">
                {t("nav.specialists", { defaultValue: "Специалисты" })}
              </Link>
              <Link to="/create-listing" className="hover:text-blue-600">
                {t("nav.post")}
              </Link>
              <Link to="/dashboard" className="hover:text-blue-600">
                {t("nav.dashboard")}
              </Link>
              <Link to="/favorites" className="hover:text-blue-600">
                {t("nav.favorites")}
              </Link>
              <Link to="/aboutus" className="hidden 2xl:inline hover:text-blue-600">
                {t("aboutUs.title")}
              </Link>
              <Link to="/turaq-kg-genplan-bishkek2050" className="hidden 2xl:inline hover:text-blue-600">
                {t("nav.genplan")}
              </Link>
              <div className="relative 2xl:hidden" ref={moreRef}>
                <button
                  type="button"
                  onClick={() => setMoreOpen((v) => !v)}
                  aria-expanded={moreOpen}
                  aria-haspopup="menu"
                  className="inline-flex items-center gap-1 font-normal hover:text-blue-600"
                >
                  {t("nav.more", { defaultValue: "Ещё" })}
                  <ChevronDown className={`h-4 w-4 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
                </button>
                {moreOpen && (
                  <div role="menu" className="absolute left-0 top-full z-50 mt-2 min-w-[180px] rounded-xl border border-gray-100 bg-white py-1.5 shadow-lg">
                    {proEnabled && (
                      <Link role="menuitem" to="/novostroyki" onClick={() => setMoreOpen(false)} className="block px-4 py-2 hover:bg-gray-50 hover:text-blue-600">
                        {t("nav.newBuildings", { defaultValue: "Новостройки" })}
                      </Link>
                    )}
                    <Link role="menuitem" to="/aboutus" onClick={() => setMoreOpen(false)} className="block px-4 py-2 hover:bg-gray-50 hover:text-blue-600">
                      {t("aboutUs.title")}
                    </Link>
                    <Link role="menuitem" to="/turaq-kg-genplan-bishkek2050" onClick={() => setMoreOpen(false)} className="block px-4 py-2 hover:bg-gray-50 hover:text-blue-600">
                      {t("nav.genplan")}
                    </Link>
                  </div>
                )}
              </div>
            </nav>

            {/* Desktop Actions */}
            <div className="hidden xl:flex items-center gap-4">
              <LanguageSelect />
              {isAuthenticated && <MessagesNavButton />}
              {isAuthenticated ? (
                <div className="relative" ref={menuRef}>
                  <div className="w-10 h-10 flex justify-center">
                    {avatar && avatar.includes("http") ? (
                      <img
                        src={avatar}
                        alt="profile-photo"
                        className="w-10 h-10 object-cover rounded-full cursor-pointer border-2 border-blue-600 hover:scale-105 transition-transform"
                        onClick={toggleProfileMenu}
                      />
                    ) : (
                      <span
                        className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center cursor-pointer border-2 border-blue-600 hover:scale-105 transition-transform"
                        onClick={toggleProfileMenu}
                      >
                        {name?.charAt(0).toUpperCase() || "U"}
                      </span>
                    )}
                  </div>

                  {profileMenuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 z-50 animate-in fade-in zoom-in-95"
                    >
                      <Link
                        to={proEnabled ? "/dashboard/account" : "/profile"}
                        role="menuitem"
                        className="flex rounded-xl items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={closeProfileMenu}
                      >
                        {t("nav.profile")}
                      </Link>
                      <Link
                        to="/bookings"
                        role="menuitem"
                        className="flex rounded-xl items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={closeProfileMenu}
                      >
                        {t("nav.myRequests", "Мои заявки")}
                      </Link>
                      <button
                        onClick={logout}
                        role="menuitem"
                        className="cursor-pointer flex rounded-xl w-full items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                      >
                        {t("nav.logout")}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link to="/login" className="hover:text-blue-600">
                    {t("nav.login")}
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    {t("nav.register")}
                  </Link>
                </>
              )}
            </div>

            {/* Mobile actions — language + menu */}
            <div className="xl:hidden flex items-center gap-2">
              <LanguageIconMenu />
              <Link
                to="/favorites"
                aria-label={t("nav.favorites", "Избранное")}
                title={t("nav.favorites", "Избранное")}
                aria-current={onFavorites ? "page" : undefined}
                className={`w-10 h-10 flex items-center justify-center rounded-full active:scale-95 transition ${
                  onFavorites ? "bg-blue-50" : "bg-gray-100"
                }`}
              >
                <Heart
                  className={`w-5 h-5 ${onFavorites ? "fill-blue-600 text-blue-600" : "text-gray-700"}`}
                />
              </Link>
              <button
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 active:scale-95 transition-transform"
                onClick={() => setMobileMenuOpen(true)}
                aria-label={t("nav.openMenu", "Открыть меню")}
              >
                <Menu className="w-6 h-6 text-gray-700" />
              </button>
            </div>
          </div>
        </div>

        {/* ─── MOBILE DRAWER ─────────────────────────────────────────── */}
        <>
          {/* Overlay */}
          <div
            className={`
      fixed inset-0 z-40
      bg-black/50 backdrop-blur-[3px]
      transition-all duration-300 ease-out
      ${mobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"}
    `}
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Drawer */}
          <div
            className={`
      fixed top-0 right-0 h-full w-4/5 max-w-sm z-50
      flex flex-col
      bg-white
      rounded-l-3xl
      pt-safe-top

      transform transition-all duration-500
      ease-[cubic-bezier(0.32,0.72,0,1)]
      ${/* closed: no shadow, or it leaks in as a grey strip on the right edge */ ""}
      ${mobileMenuOpen ? "translate-x-0 visible shadow-[0_30px_80px_rgba(0,0,0,0.25)]" : "translate-x-full invisible shadow-none"}
    `}
          >
            <div className="flex flex-col h-full p-6">
              {/* HEADER */}
              <div className="flex items-center justify-between mb-6">
                <Link
                  to="/"
                  className="
            flex items-center gap-2 text-2xl font-bold
            active:scale-95 transition
          "
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    TURAQ.KG
                  </span>
                </Link>

                <button
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Закрыть"
                  className="
            w-10 h-10 flex items-center justify-center
            rounded-full
            bg-gray-100
            hover:bg-gray-200
            active:scale-90
            transition-all duration-200
          "
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* USER BLOCK */}
              {isAuthenticated && (
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="
            flex items-center gap-3 mb-5 p-3
            bg-gradient-to-r from-blue-50 to-indigo-50
            rounded-2xl
            border border-blue-100
            active:scale-[0.98]
            transition-all duration-200
          "
                >
                  {avatar && avatar.includes("http") ? (
                    <img
                      src={avatar}
                      alt="avatar"
                      className="
                w-11 h-11 rounded-full object-cover
                border-2 border-blue-600
              "
                    />
                  ) : (
                    <span
                      className="
              bg-blue-600 text-white rounded-full
              w-11 h-11 flex items-center justify-center
              text-lg font-bold
            "
                    >
                      {name?.charAt(0).toUpperCase() || "U"}
                    </span>
                  )}

                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">
                      {name}
                    </p>
                    <p className="text-xs text-blue-600">{t("nav.profile")}</p>
                  </div>
                </Link>
              )}

              {/* NAV */}
              <nav className="flex flex-col gap-2 flex-grow overflow-y-auto">
                {mobileDrawerNavArray.map(({ to, label, icon: Icon }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setMobileMenuOpen(false)}
                    className="
              group flex items-center gap-3 p-3 rounded-xl
              bg-gray-50
              hover:bg-blue-50
              active:scale-[0.97]
              transition-all duration-200
            "
                  >
                    <Icon
                      className="
              w-5 h-5 text-blue-600 shrink-0
              transition-transform duration-200
              group-hover:scale-110
            "
                    />
                    <span className="text-gray-800 font-medium">{label}</span>
                    {to === "/messages" && unreadBadge && (
                      <span className="ml-auto min-w-[22px] rounded-full bg-blue-600 px-1.5 py-0.5 text-center text-xs font-bold text-white">
                        {unreadBadge}
                      </span>
                    )}
                  </Link>
                ))}

                {/* LOGOUT */}
                {isAuthenticated && (
                  <button
                    type="button"
                    onClick={logout}
                    className="
              flex items-center gap-3 p-3 rounded-xl
              bg-red-50
              hover:bg-red-100
              active:scale-[0.97]
              transition-all duration-200
              mt-1 w-full text-left
            "
                  >
                    <LogOut className="w-5 h-5 text-red-500 shrink-0" />
                    <span className="text-red-600 font-medium">
                      {t("nav.logout")}
                    </span>
                  </button>
                )}
              </nav>

              {/* FOOTER */}
              <div className="pt-4 border-t border-gray-200 mt-4">
                <LanguageSelect />
              </div>
            </div>
          </div>
        </>
      </header>

      {/* BOTTOM NAV FOR MOBILE */}
      {proEnabled ? <ProTabBar mobileMenuOpen={mobileMenuOpen} /> : <BottomNav mobileMenuOpen={mobileMenuOpen} />}
    </>
  );
}
